import { useState, useEffect, useRef, useMemo } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { formatFileSize } from '@/lib/schemas'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import {
  Folder,
  Plus,
  ChevronRight,
  CheckCircle2,
  X,
  Upload,
} from 'lucide-react'

const CREATE_NEW_SENTINEL = '__create_new__'

interface UploadDestinationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Files staged for upload. */
  files: File[]
  /** Initial destination for the default-mode dropdown. */
  defaultFolderId?: string | null
}

// ============================================================================
// Folder picker with inline "Create new folder" flow
// ============================================================================

interface FolderSelectProps {
  value: string
  onChange: (folderId: string) => void
  className?: string
}

/**
 * Select that lists all folders + a "Create new folder…" sentinel item.
 * Picking the sentinel swaps the trigger for an inline name input (root-only).
 * The new folder is created via the store and immediately selected.
 */
function FolderSelect({ value, onChange, className }: FolderSelectProps) {
  const { folders, createFolder } = useLibraryStore()
  const [isCreating, setIsCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isCreating) inputRef.current?.focus()
  }, [isCreating])

  const sortedFolders = useMemo(() => {
    return [...folders].sort((a, b) => a.name.localeCompare(b.name))
  }, [folders])

  const handleChange = (v: string) => {
    if (v === CREATE_NEW_SENTINEL) {
      setIsCreating(true)
      setNewName('')
    } else {
      onChange(v)
    }
  }

  const handleConfirm = async () => {
    const trimmed = newName.trim()
    if (!trimmed) return
    setIsSaving(true)
    try {
      const created = await createFolder(trimmed, null, 'other')
      onChange(created.id)
      setIsCreating(false)
      setNewName('')
    } catch {
      // Error surfaced via store toast
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setIsCreating(false)
    setNewName('')
  }

  if (isCreating) {
    return (
      <div className={cn('flex items-center gap-1', className)}>
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleConfirm()
              else if (e.key === 'Escape') handleCancel()
            }}
            placeholder="New folder name"
            disabled={isSaving}
            className="h-8 text-sm pr-12"
          />
          <Badge
            variant="secondary"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] px-1 py-0 h-4 font-normal pointer-events-none"
          >
            Root
          </Badge>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0"
          onClick={() => void handleConfirm()}
          disabled={!newName.trim() || isSaving}
          title="Create folder at root level"
        >
          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--brand))]" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 shrink-0"
          onClick={handleCancel}
          disabled={isSaving}
          title="Cancel"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
    )
  }

  return (
    <Select value={value} onValueChange={handleChange}>
      <SelectTrigger className={cn('h-8 text-sm', className)}>
        <SelectValue placeholder="Pick a folder" />
      </SelectTrigger>
      <SelectContent>
        {sortedFolders.map((f) => (
          <SelectItem key={f.id} value={f.id}>
            <span className="flex items-center gap-2">
              <Folder className="h-3.5 w-3.5 text-muted-foreground" />
              {f.name}
            </span>
          </SelectItem>
        ))}
        <div className="my-1 h-px bg-border" />
        <SelectItem value={CREATE_NEW_SENTINEL}>
          <span className="flex items-center gap-2 text-[hsl(var(--brand))]">
            <Plus className="h-3.5 w-3.5" />
            Create new folder…
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  )
}

// ============================================================================
// Main dialog
// ============================================================================

export function UploadDestinationDialog({
  open,
  onOpenChange,
  files,
  defaultFolderId,
}: UploadDestinationDialogProps) {
  const { folders, uploadLibraryDocuments } = useLibraryStore()

  // Default destination falls back to the first folder if none provided
  const initialDefault =
    defaultFolderId && folders.some((f) => f.id === defaultFolderId)
      ? defaultFolderId
      : folders[0]?.id ?? ''

  const [defaultDest, setDefaultDest] = useState<string>(initialDefault)
  const [perFile, setPerFile] = useState(false)
  const [perFileMap, setPerFileMap] = useState<Record<number, string>>({})
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [isUploading, setIsUploading] = useState(false)

  // Reset all state when the dialog re-opens
  useEffect(() => {
    if (open) {
      setDefaultDest(initialDefault)
      setPerFile(false)
      setPerFileMap({})
      setSelectedRows(new Set())
    }
    // initialDefault is derived from folders + defaultFolderId, both stable for the dialog session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const getDestForFile = (idx: number) =>
    perFile ? perFileMap[idx] ?? defaultDest : defaultDest

  const distinctDestCount = useMemo(() => {
    if (!perFile) return 1
    const set = new Set<string>()
    files.forEach((_, i) => set.add(getDestForFile(i)))
    return set.size
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perFile, perFileMap, defaultDest, files])

  const toggleRow = (idx: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const toggleAll = () => {
    if (selectedRows.size === files.length) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(files.map((_, i) => i)))
    }
  }

  const applyBulk = (folderId: string) => {
    if (folderId === CREATE_NEW_SENTINEL || selectedRows.size === 0) return
    setPerFileMap((prev) => {
      const next = { ...prev }
      selectedRows.forEach((idx) => {
        next[idx] = folderId
      })
      return next
    })
    // Clear selection after apply so the bulk bar collapses
    setSelectedRows(new Set())
  }

  const handleUpload = async () => {
    setIsUploading(true)
    try {
      // Group files by destination, upload each group
      const groups = new Map<string, File[]>()
      files.forEach((file, idx) => {
        const dest = getDestForFile(idx)
        if (!dest) return
        const arr = groups.get(dest) ?? []
        arr.push(file)
        groups.set(dest, arr)
      })

      for (const [folderId, groupFiles] of groups.entries()) {
        await uploadLibraryDocuments(folderId, groupFiles)
      }
      onOpenChange(false)
    } finally {
      setIsUploading(false)
    }
  }

  const canUpload = files.length > 0 && Boolean(defaultDest)
  const allSelected = selectedRows.size === files.length && files.length > 0
  const someSelected = selectedRows.size > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Where should these files go?</DialogTitle>
          <DialogDescription>
            {files.length} {files.length === 1 ? 'file' : 'files'} ready to upload.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {/* Default destination (only in compact mode) */}
          {!perFile && (
            <div className="flex items-center gap-3">
              <Label className="text-sm w-24 shrink-0">Destination</Label>
              <FolderSelect
                value={defaultDest}
                onChange={setDefaultDest}
                className="flex-1"
              />
            </div>
          )}

          {/* Per-file toggle */}
          <button
            type="button"
            onClick={() => {
              setPerFile((p) => !p)
              setSelectedRows(new Set())
            }}
            className="text-xs text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] flex items-center gap-1"
          >
            <ChevronRight
              className={cn(
                'h-3 w-3 transition-transform',
                perFile && 'rotate-90'
              )}
            />
            {perFile
              ? 'Use one destination for all files'
              : 'Set destination per file'}
          </button>

          {/* Bulk-apply bar — only in per-file mode */}
          {perFile && (
            <div
              className={cn(
                'flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors',
                someSelected
                  ? 'bg-[hsl(var(--brand-soft))] border-[hsl(var(--brand)/0.3)]'
                  : 'bg-muted/30 border-border'
              )}
            >
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
                className={cn(!someSelected && !allSelected && 'opacity-50')}
                aria-label="Select all files"
              />
              {someSelected ? (
                <>
                  <span className="text-sm font-medium">
                    {selectedRows.size} selected
                  </span>
                  <span className="text-sm text-muted-foreground">
                    Set destination:
                  </span>
                  <FolderSelect
                    value=""
                    onChange={applyBulk}
                    className="flex-1 max-w-xs"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 ml-auto"
                    onClick={() => setSelectedRows(new Set())}
                  >
                    Clear
                  </Button>
                </>
              ) : (
                <span className="text-meta">
                  Tick rows to set a destination for multiple files at once.
                </span>
              )}
            </div>
          )}

          {/* File list */}
          <div className="rounded-lg border bg-muted/20 max-h-72 overflow-auto">
            {files.map((file, idx) => {
              const destId = getDestForFile(idx)
              const destFolder = folders.find((f) => f.id === destId)
              const isRowSelected = selectedRows.has(idx)
              return (
                <div
                  key={idx}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 transition-colors',
                    idx > 0 && 'border-t border-border',
                    isRowSelected && 'bg-[hsl(var(--brand-soft))]'
                  )}
                >
                  {perFile && (
                    <Checkbox
                      checked={isRowSelected}
                      onCheckedChange={() => toggleRow(idx)}
                      aria-label={`Select ${file.name}`}
                    />
                  )}
                  <FileTypeIcon mimeType={file.type} size={20} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-meta">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  {perFile ? (
                    <FolderSelect
                      value={destId}
                      onChange={(v) =>
                        setPerFileMap((prev) => ({ ...prev, [idx]: v }))
                      }
                      className="w-48 shrink-0"
                    />
                  ) : (
                    <div className="flex items-center gap-1.5 text-meta shrink-0">
                      <ChevronRight className="h-3 w-3" />
                      <Folder className="h-3.5 w-3.5" />
                      <span className="truncate max-w-[140px]">
                        {destFolder?.name ?? '—'}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Summary */}
          {perFile && (
            <p className="text-meta">
              {files.length} {files.length === 1 ? 'file' : 'files'} going to{' '}
              {distinctDestCount} {distinctDestCount === 1 ? 'folder' : 'folders'}.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleUpload()}
            disabled={!canUpload || isUploading}
          >
            <Upload className="h-4 w-4 mr-2" />
            {isUploading
              ? 'Uploading…'
              : `Upload ${files.length} ${files.length === 1 ? 'file' : 'files'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
